import { useState, useRef, useEffect, useCallback } from "react";
import { Avatar, Box, Skeleton, Typography } from "@mui/material";
import { Scrollbars } from "react-custom-scrollbars-2";
import { makeStyles } from "@mui/styles";
import UserChatBubble from "../UserChatBubble";
import BotChatBubble from "../BotChatBubble";
import ChatBubble from "../Bubble";
import { useConfig } from "../../../contexts/ConfigContext";
import styles from "../styles";

function MessageFeed({
  messageHistory,
  handleFeedbackSubmit,
  isStreaming,
  isFeedbackType,
  scrollCallback,
}) {
  const useStyles = makeStyles(styles);
  const { config } = useConfig();
  const classes = useStyles();
  const scrollRef = useRef(null);

  const [lastScrollHeight, setLastScrollHeight] = useState(null);
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
  const lastScrollTop = useRef(0);

  const handleScroll = useCallback((scrollData) => {
    const { scrollTop, scrollHeight, clientHeight } = scrollData;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
    const isScrollingUp = scrollTop < lastScrollTop.current;
    
    if (isScrollingUp && !isAtBottom) {
      setUserHasScrolledUp(true);
    }
    else if (isAtBottom) {
      setUserHasScrolledUp(false);
    }
    
    lastScrollTop.current = scrollTop;
    scrollCallback(scrollData, (position) => setLastScrollHeight(position));
  }, [scrollCallback]);

  useEffect(() => {
    if (!messageHistory) return;
    const scroller = scrollRef.current;
    const scrollHeight = scroller.getScrollHeight();

    if (lastScrollHeight && scrollHeight !== lastScrollHeight) {
      scroller.scrollTop(scrollHeight - lastScrollHeight);
      setLastScrollHeight(null);
      return;
    }

    if (!userHasScrolledUp) {
      scrollToBottom();
    }
  }, [messageHistory, userHasScrolledUp, lastScrollHeight]);

  useEffect(() => {
    if (!isStreaming) {
      setUserHasScrolledUp(false);
    }
  }, [isStreaming]);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current?.scrollToBottom();
    }
  };

  return (
    <Scrollbars
      ref={(scrollBarRef) => (scrollRef.current = scrollBarRef)}
      style={{ height: "calc(100% - 110px)" }}
      onScrollFrame={handleScroll}
    >
      <Box className={classes.messageHolder}>
        {messageHistory ? (
          messageHistory?.messages?.map((message, index) =>
            message?.origin === "user" ? (
              <UserChatBubble
                key={message?.timestamp}
                message={message}
                isFeedbackType={isFeedbackType}
              />
            ) : message?.origin === "bot" ? (
              <>
                {messageHistory.messages[index - 1]?.origin === "user" && (
                  <Box
                    sx={{
                      margin: "auto",
                      width: "94%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 22,
                        height: 22,
                        mr: 1,
                        background: config.color.avatarBackground,
                      }}
                    >
                      <img
                        alt="bot avatar"
                        style={{ maxWidth: "16px", maxHeight: "16px" }}
                        src={config.botAvatarImage}
                      />
                    </Avatar>
                    <Typography color="text.secondary" variant="caption">
                      {config.botName}
                    </Typography>
                  </Box>
                )}
                <BotChatBubble
                  key={message?.timestamp}
                  message={message}
                  handleFeedbackSubmit={handleFeedbackSubmit}
                  isFeedbackType={isFeedbackType}
                />
              </>
            ) : (
              <ChatBubble variant={message.origin} message={message} />
            )
          )
        ) : (
          <Box
            sx={{ width: "95%", maxWidth: "950px", margin: "auto" }}
            display="flex"
            flexDirection="column"
          >
            {[...new Array(6)].map((idx) => (
              <Box display="flex" key={idx}>
                <Skeleton
                  variant="rectangular"
                  height={100}
                  width="100%"
                  sx={{
                    borderRadius: 1.5,
                    mb: 2,
                    background: "#fff",
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Scrollbars>
  );
}

export default MessageFeed;
